import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { Skeleton, useToast } from "@alchema/ui";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/jobs/scraped")({
  beforeLoad: async () => { await requireAuth(); },
  component: ScrapedJobsPage,
});

const SOURCE_COLORS: Record<string, string> = {
  linkedin: "bg-status-info-bg text-status-info",
  indeed: "bg-status-purple-bg text-status-purple",
  stepstone: "bg-status-success-bg text-status-success",
  xing: "bg-status-info-bg text-status-info",
};

function scoreColor(score: number | null): string {
  if (score === null) return "bg-[var(--bg-secondary)] text-[var(--text-muted)]";
  if (score >= 80) return "bg-status-success-bg text-status-success";
  if (score >= 60) return "bg-status-warning-bg text-status-warning";
  return "bg-status-error-bg text-status-error";
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}

function JobTabs() {
  const location = useLocation();
  const { data: newCount } = useQuery({
    ...orpc.jobSearch.newJobCount.queryOptions(),
    staleTime: 60_000,
    retry: 0,
  });

  const tabs = [
    { label: m.jobs_tab_my_jobs(), to: "/jobs" },
    { label: m.jobs_tab_scraped(), to: "/jobs/scraped", badge: newCount?.count },
    { label: m.jobs_tab_search_criteria(), to: "/jobs/search-criteria" },
  ];

  return (
    <div className="mb-6 flex gap-1 rounded-lg bg-[var(--bg-secondary)] p-1">
      {tabs.map((tab) => {
        const isActive = location.pathname === tab.to;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={`relative rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-[var(--surface)] text-[var(--text)] shadow-[var(--shadow-sm)]" : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
            }`}
          >
            {tab.label}
            {tab.badge && tab.badge > 0 ? (
              <span className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-status-error px-1.5 text-[10px] font-bold text-white">
                {tab.badge}
              </span>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
}

function ScrapedJobsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [sourceFilter, setSourceFilter] = React.useState<string>("all");
  const [minScore, setMinScore] = React.useState<number>(0);
  const [newOnly, setNewOnly] = React.useState(false);
  const [criteriaFilter, setCriteriaFilter] = React.useState<string>("all");

  const criteriaQuery = useQuery({
    ...orpc.jobSearch.listCriteria.queryOptions(),
    staleTime: 60_000,
  });

  const jobsQuery = useQuery(orpc.jobSearch.listScrapedJobs.queryOptions({
    input: {
      ...(sourceFilter !== "all" ? { source: sourceFilter as "linkedin" | "indeed" | "stepstone" | "xing" } : {}),
      ...(minScore > 0 ? { minScore } : {}),
      ...(newOnly ? { isNew: true } : {}),
      ...(criteriaFilter !== "all" ? { criteriaId: criteriaFilter } : {}),
    },
  }));

  const saveMutation = useMutation(
    orpc.jobSearch.saveToJobs.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.jobSearch.listScrapedJobs.key({}) });
        queryClient.invalidateQueries({ queryKey: orpc.jobSearch.newJobCount.key({}) });
        queryClient.invalidateQueries({ queryKey: orpc.job.list.key({}) });
        toast(m.jobs_saved_to_list(), "success");
      },
      onError: (err) => toast(err.message || "Failed to save job", "error"),
    }),
  );

  const jobs = jobsQuery.data ?? [];

  return (
    <div className="mx-auto max-w-4xl p-8">
      <JobTabs />
      <h1 className="text-2xl font-bold">{m.jobs_scraped_jobs()}</h1>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{m.jobs_results_count({ count: jobs.length, plural: jobs.length !== 1 ? "s" : "" })}</p>

      {/* Filter bar */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
        >
          <option value="all">{m.jobs_all_sources()}</option>
          <option value="linkedin">LinkedIn</option>
          <option value="indeed">Indeed</option>
          <option value="stepstone">StepStone</option>
          <option value="xing">XING</option>
        </select>

        {criteriaQuery.data && criteriaQuery.data.length > 0 && (
          <select
            value={criteriaFilter}
            onChange={(e) => setCriteriaFilter(e.target.value)}
            className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
          >
            <option value="all">{m.jobs_all_criteria()}</option>
            {criteriaQuery.data.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        <div className="flex items-center gap-2">
          <label className="text-sm text-[var(--text-muted)]">{m.jobs_min_score()}</label>
          <input
            type="number"
            min={0}
            max={100}
            value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="w-16 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-2 text-sm"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <input
            type="checkbox"
            checked={newOnly}
            onChange={(e) => setNewOnly(e.target.checked)}
            className="rounded border-[var(--border)]"
          />
          {m.jobs_new_only()}
        </label>
      </div>

      {/* Job list */}
      {jobsQuery.isLoading ? (
        <div className="mt-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="mt-12 text-center">
          <p className="text-[var(--text-muted)]">{m.jobs_no_scraped_jobs()}</p>
          <Link to="/jobs/search-criteria" className="mt-2 inline-block text-sm font-medium text-[var(--text)] hover:underline">
            {m.jobs_search_criteria_setup_link()}
          </Link>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {jobs.map((job) => (
            <div key={job.id} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 shadow-[var(--shadow-sm)]">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-sm font-semibold">{job.title}</h3>
                    {job.isNew && (
                      <span className="inline-flex h-5 items-center rounded-full bg-status-info-bg px-2 text-[10px] font-bold text-status-info">
                        {m.jobs_new_label()}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                    {job.company}
                    {job.location ? ` \u00B7 ${job.location}` : ""}
                    {job.postedAt ? ` \u00B7 ${relativeTime(job.postedAt)}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${SOURCE_COLORS[job.source] || ""}`}>
                    {job.source}
                  </span>
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${scoreColor(job.atsScore)}`}>
                    {job.atsScore !== null ? `${job.atsScore}%` : "N/A"}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => saveMutation.mutate({ scrapedJobId: job.id })}
                  disabled={saveMutation.isPending}
                  className="rounded-lg bg-[var(--text)] px-3 py-1.5 text-xs font-medium text-[var(--text-inverse)] hover:opacity-90 disabled:opacity-50"
                >
                  {m.jobs_save_to_jobs()}
                </button>
                <a
                  href={job.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]"
                >
                  {m.jobs_view()}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
